import Button from "@/components/Button";
import ErrorsWidget from "@/components/ErrorsWidget";
import { NumberInput, SelectInput } from "@/components/InputFields";
import Spinner from "@/components/Spinner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/chadcn/Dialog";
import { useSelfUserQuery } from "@/data/queries";
import { DiabetesProtocol, PersonalizedDiabetesProtocol } from "@/model/ClinicalProtocol";
import { contains } from "@/util/array-utils";
import { Instant } from "@js-joda/core";
import { revalidateLogic, useForm, useStore } from "@tanstack/react-form";
import { isEqual, isMatch } from "lodash";
import { useRef, useState } from "react";

type FormValues = {
  selectedProtocolIndex: number;
  emergencyLow: number;
  criticalLow: number;
  fastingElevated: number;
  postprandialLow: number;
  postprandialElevated: number; // = just elevated
  criticalHigh: number;
};

type UpdateDiabetesProtocolDialogProps = {
  personalizedClinicalProtocol: PersonalizedDiabetesProtocol;
  diagnosis?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading: boolean;
  isError: boolean;
  onSubmit: (values: PersonalizedDiabetesProtocol) => Promise<void>;
  organizationClinicalProtocols: DiabetesProtocol[];
  canEditThresholds?: boolean;
};

export const UpdateDiabetesProtocolDialog = ({
  personalizedClinicalProtocol,
  organizationClinicalProtocols,
  diagnosis,
  open,
  onOpenChange,
  isError: isSubmitError,
  onSubmit,
  canEditThresholds = true,
}: UpdateDiabetesProtocolDialogProps) => {
  const { data: user } = useSelfUserQuery();

  const personalizedProtocol: DiabetesProtocol = {
    name: personalizedClinicalProtocol.name,
    type: "diabetes",
    thresholds: personalizedClinicalProtocol.thresholds,
  };

  const hasCustomProtocol = !contains(organizationClinicalProtocols, personalizedProtocol);

  const protocols = hasCustomProtocol
    ? [personalizedProtocol, ...organizationClinicalProtocols]
    : organizationClinicalProtocols;

  const protocolOptions = protocols.map((protocol, index) => ({
    value: index,
    title:
      hasCustomProtocol && isEqual(protocol, personalizedProtocol)
        ? `${protocol.name}*`
        : protocol.name,
  }));

  const initialValues: FormValues = {
    selectedProtocolIndex: protocols.findIndex((protocol) =>
      isEqual(protocol, personalizedProtocol)
    )!,
    ...personalizedProtocol.thresholds,
  };

  const [pulsingFields, setPulsingFields] = useState<Set<keyof FormValues>>(() => new Set());
  const pulseTimeoutRef = useRef<number | null>(null);

  const onSubmitInternal = async ({ value }: { value: FormValues }) => {
    const selectedProtocol = protocols[value.selectedProtocolIndex]!;
    await onSubmit({
      name: selectedProtocol.name,
      type: "diabetes",
      thresholds: {
        emergencyLow: value.emergencyLow,
        criticalLow: value.criticalLow,
        fastingElevated: value.fastingElevated,
        postprandialLow: value.postprandialLow,
        postprandialElevated: value.postprandialElevated,
        criticalHigh: value.criticalHigh,
      },
      lastUpdatedAt: Instant.now(),
      lastUpdatedBy: user!,
    });
    form.reset();
  };

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: onSubmitInternal,
    validationLogic: revalidateLogic(),
    validators: {
      onChange: ({ value }) => {
        const errors = [
          !isFinite(value.emergencyLow)
            ? "Emergency low is required"
            : value.emergencyLow < 0
              ? "Emergency low must be greater than 0"
              : null,
          !isFinite(value.criticalLow)
            ? "Critical low is required"
            : isFinite(value.emergencyLow) && value.criticalLow < value.emergencyLow
              ? "Critical low must be greater than or equal to emergency low"
              : null,
          !isFinite(value.fastingElevated)
            ? "Fasting elevated is required"
            : isFinite(value.criticalLow) && value.fastingElevated <= value.criticalLow
              ? "Fasting elevated must be greater than critical low"
              : null,
          !isFinite(value.postprandialLow)
            ? "Postprandial low is required"
            : isFinite(value.criticalLow) && value.postprandialLow < value.criticalLow
              ? "Postprandial low must be greater than or equal to critical low"
              : null,
          !isFinite(value.postprandialElevated)
            ? "Postprandial elevated is required"
            : isFinite(value.fastingElevated) && value.postprandialElevated < value.fastingElevated
              ? "Postprandial elevated must be greater than or equal to fasting elevated"
              : null,
          !isFinite(value.criticalHigh)
            ? "Critical high is required"
            : isFinite(value.postprandialElevated) &&
                value.criticalHigh < value.postprandialElevated
              ? "Critical high must be greater than or equal to postprandial elevated"
              : null,
        ].filter(Boolean);

        return errors.length > 0 ? errors : null;
      },
    },
  });

  const { emergencyLow, criticalLow, criticalHigh } = useStore(form.store, ({ values }) => values);

  const canSubmit = useStore(form.store, ({ values, canSubmit }) => {
    const hasChanges = !isMatch(initialValues, values);
    return hasChanges && canSubmit;
  });

  const handleOnOpenChange = (open: boolean) => {
    form.reset();
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOnOpenChange}>
      <DialogContent className="flex w-[600px] flex-col overflow-hidden bg-white">
        <DialogHeader className="mb-6">
          <span className="flex flex-row items-center gap-4">
            <DialogTitle className="mb-1 truncate text-3xl font-bold">Adjust protocol</DialogTitle>
            <form.Field
              name="selectedProtocolIndex"
              children={(field) => (
                <SelectInput
                  className="w-fit"
                  id="protocol-select"
                  value={field.state.value}
                  options={protocolOptions}
                  onChange={(selectedProtocolIndex) => {
                    const selectedProtocol = protocols[selectedProtocolIndex]!;
                    const newValues = {
                      selectedProtocolIndex,
                      ...selectedProtocol.thresholds,
                    };
                    const changed = new Set<keyof FormValues>();
                    const current = form.state.values as FormValues;
                    (Object.keys(selectedProtocol.thresholds) as (keyof FormValues)[]).forEach(
                      (key) => {
                        if (
                          typeof current[key] === "number" &&
                          typeof newValues[key] === "number" &&
                          current[key] !== newValues[key]
                        ) {
                          changed.add(key);
                        }
                      }
                    );
                    form.reset(newValues);
                    field.handleChange(selectedProtocolIndex);
                    setPulsingFields(changed);
                    if (pulseTimeoutRef.current) {
                      window.clearTimeout(pulseTimeoutRef.current);
                    }
                    pulseTimeoutRef.current = window.setTimeout(() => {
                      setPulsingFields(new Set());
                    }, 1000);
                  }}
                />
              )}
            />
          </span>
          <div className="flex flex-row justify-between">
            {diagnosis && <p className="truncate">Diagnosis: {diagnosis}</p>}
          </div>
        </DialogHeader>
        <form
          className="flex flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className="mb-6 grid grid-cols-[1fr_1fr] gap-x-10 gap-y-1">
            <div className="flex flex-col">
              <h1 className="mb-2 text-lg font-semibold">Fasting protocol</h1>
              <div className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-1">
                <label className="py-px">Emergency low:</label>
                <form.Field
                  name="emergencyLow"
                  children={(field) => (
                    <div className="flex items-center gap-2">
                      <span className="py-px">≤</span>
                      <NumberInput
                        name={field.name}
                        disabled={!canEditThresholds}
                        className={
                          "w-14 px-2 py-px " +
                          (pulsingFields.has("emergencyLow")
                            ? "animate-pulse bg-blue-100 ring-blue-600"
                            : "")
                        }
                        value={isFinite(field.state.value) ? field.state.value : ""}
                        onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                        onBlur={() => field.handleBlur()}
                      />
                    </div>
                  )}
                />

                <span className="py-px">Critical low:</span>
                <div className="flex">
                  <span className="me-2 py-px">≤</span>
                  <form.Field
                    name="criticalLow"
                    children={(field) => (
                      <NumberInput
                        name={field.name}
                        disabled={!canEditThresholds}
                        className={
                          "w-14 px-2 py-px " +
                          (pulsingFields.has("criticalLow")
                            ? "animate-pulse bg-blue-100 ring-blue-600"
                            : "")
                        }
                        value={isFinite(field.state.value) ? field.state.value : ""}
                        onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                        onBlur={() => field.handleBlur()}
                      />
                    )}
                  />
                </div>
                <span className="py-px">-</span>
                <div />
                <span className="py-px">Elevated:</span>
                <div className="flex items-center">
                  <span className="me-2 py-px">≥</span>
                  <form.Field
                    name="fastingElevated"
                    children={(field) => (
                      <NumberInput
                        name={field.name}
                        disabled={!canEditThresholds}
                        className={
                          "w-14 px-2 py-px " +
                          (pulsingFields.has("fastingElevated")
                            ? "animate-pulse bg-blue-100 ring-blue-600"
                            : "")
                        }
                        value={isFinite(field.state.value) ? field.state.value : ""}
                        onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                        onBlur={() => field.handleBlur()}
                      />
                    )}
                  />
                </div>
                <span className="py-px">Critical high:</span>
                <div className="flex items-center">
                  <span className="me-2 py-px">≥</span>
                  <form.Field
                    name="criticalHigh"
                    children={(field) => (
                      <NumberInput
                        name={field.name}
                        disabled={!canEditThresholds}
                        className={
                          "w-14 px-2 py-px " +
                          (pulsingFields.has("criticalHigh")
                            ? "animate-pulse bg-blue-100 ring-blue-600"
                            : "")
                        }
                        value={isFinite(field.state.value) ? field.state.value : ""}
                        onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                        onBlur={() => field.handleBlur()}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              <h1 className="mb-2 text-lg font-semibold">Postprandial protocol</h1>
              <div className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-1">
                <label className="py-px">Emergency low:</label>
                <div className="flex items-center gap-2">
                  <span className="py-px">≤</span>
                  <span className="w-14 truncate px-2 py-px">
                    {isFinite(emergencyLow) ? emergencyLow : ""}
                  </span>
                </div>
                <span className="py-px">Critical low:</span>
                <div className="flex items-center">
                  <span className="me-2 py-px">≤</span>
                  <span className="w-14 truncate px-2 py-px">
                    {isFinite(criticalLow) ? criticalLow : ""}
                  </span>
                </div>
                <span className="py-px">Low:</span>
                <div className="flex items-center">
                  <span className="me-2 py-px">≤</span>
                  <form.Field
                    name="postprandialLow"
                    children={(field) => (
                      <NumberInput
                        name={field.name}
                        disabled={!canEditThresholds}
                        className={
                          "w-14 px-2 py-px " +
                          (pulsingFields.has("postprandialLow")
                            ? "animate-pulse bg-blue-100 ring-blue-600"
                            : "")
                        }
                        value={isFinite(field.state.value) ? field.state.value : ""}
                        onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                        onBlur={() => field.handleBlur()}
                      />
                    )}
                  />
                </div>
                <span className="py-px">Elevated:</span>
                <div className="flex items-center">
                  <span className="me-2 py-px">≥</span>
                  <form.Field
                    name="postprandialElevated"
                    children={(field) => (
                      <NumberInput
                        name={field.name}
                        disabled={!canEditThresholds}
                        className={
                          "w-14 px-2 py-px " +
                          (pulsingFields.has("postprandialElevated")
                            ? "animate-pulse bg-blue-100 ring-blue-600"
                            : "")
                        }
                        value={isFinite(field.state.value) ? field.state.value : ""}
                        onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                        onBlur={() => field.handleBlur()}
                      />
                    )}
                  />
                </div>
                <span className="py-px">Critical high:</span>
                <div className="flex items-center">
                  <span className="me-2 py-px">≥</span>
                  <span className="w-14 truncate px-2 py-px">
                    {isFinite(criticalHigh) ? criticalHigh : ""}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <p className="mb-4 italic">
            Note: Updated thresholds will apply only to new measurements
          </p>
          <form.Subscribe
            selector={(state) => state.errorMap.onChange}
            children={(onChange) => {
              return onChange && onChange?.length !== 0 ? (
                <ErrorsWidget
                  errorMessages={onChange}
                  format={(errorMessage) => errorMessage}
                  className="mb-4"
                />
              ) : null;
            }}
          />
          {isSubmitError && (
            <p className="text-red-600">
              There was an error submitting the new values. Please contact Bloomlife Support, or try
              again later.
            </p>
          )}
          <form.Subscribe
            selector={(state) => state.isSubmitting}
            children={(isSubmitting) => (
              <DialogFooter>
                <Button variant="primary" type="submit" disabled={!canSubmit} className="w-fit">
                  Change
                  {isSubmitting && <Spinner className="ms-2 size-4 border-2" />}
                </Button>
              </DialogFooter>
            )}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
};
